import { useCallback, useState, type FormEvent, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, ClipboardCheck, ShieldAlert, ShieldCheck, UserMinus, UserPlus, X } from 'lucide-react'
import { ctaButton, dangerStyle, outlineButton, panelBox, primaryButton, quietButton, secondaryButton, small } from '@/components/shell/StatusScreens'
import { Drawer } from '@/components/ui/Drawer'
import { ExampleBadge } from '@/components/ui/ExampleBadge'
import { CompactList, FiltersPopover, HowItWorks, SearchBox, SegmentChips, Pager, StateHeader, usePager } from '@/features/erp/ui/calm'
import { ApiError } from '@/lib/api'
import { useNow } from '@/lib/useNow'
import { cn } from '@/lib/utils'
import { adminAreaQuery, adminMeQuery, meCan, useAdminAction } from '../shared/api'
import { ROLE_LABELS, ADMIN_ROLES, type AdminRole } from '../shared/permissions'
import { AreaState, Card, Chip, Segmented, Tabs, type Tone } from '../shared/ui'
import {
    ADMIN_HOURS, LOG_KINDS, type ActivateInput, type CancelInviteInput, type EquipeData, type InviteInput, type InviteResult, type LogKind, type OffboardInput, type OffboardResult,
    type Person,
} from './contract'
import {
    LOG_LABELS, PERMISSION_GROUPS, PERMISSION_LABELS, ROLE_COLUMNS, filterLog, filterPeople, remaining, reviewOverdue, roleHas, shortDate, teamSentence,
    validityLabel,
    type PeopleFilter,
} from './equipe-logic'

/* Spec COG-009 — Admin › Equipe e permissões (prototype AdminEquipe.dc.html). On top: who left in HR, KPIs, the admin
   role activated for a time and the access review. Below, tabs Pessoas · Papéis e permissões · Registro. A person opens in
   a side panel (data, "Cancelar convite", "Desligar acesso" with its checklist — no undo); "Convidar pessoa" is a side
   panel too and its toast offers "Desfazer" (cancels the pending invite). Example data: no change to PANEL_ADMIN_ROLES yet.
   Calm layout (ERP SPEC part 5): one sentence on who has access, 3 numbers in the pulse, one search + the "who" chips
   (role and area in "Filtros"), compact rows 20 at a time and the rules in "Como funciona". */

/** `group`/`intro`: a mesma tela embutida em Configurações › Usuários e acessos (ERP), com uma nota acima. */
export function EquipePage({ group = 'Gestão', intro }: { group?: string; intro?: ReactNode } = {}) {
    const query = useQuery(adminAreaQuery<EquipeData>('equipe'))
    return (
        <>
            {/* The body draws the header with the live sentence; while loading (or without access) only the name shows */}
            {!query.data && <StateHeader eyebrow={group} title="Equipe e permissões" />}
            {!query.data && intro}
            <AreaState query={query} noAccess={{ title: 'Você não vê a equipe', text: 'Fica com a Tecnologia e a gerência. Peça a um administrador.' }}>
                {data => <EquipeBody data={data} group={group} intro={intro} />}
            </AreaState>
        </>
    )
}

type Section = 'pessoas' | 'papeis' | 'registro'

function EquipeBody({ data, group, intro }: { data: EquipeData; group: string; intro?: ReactNode }) {
    const me = useQuery(adminMeQuery).data
    const now = useNow(30_000).getTime()
    const canManage = meCan(me, 'equipe.gerenciar')
    const adminLeft = remaining(data.adminSession.until, now)
    const managing = canManage && data.adminSession.active && !!adminLeft
    const [section, setSection] = useState<Section>('pessoas')
    const [view, setView] = useState<PeopleFilter['view']>('ativos')

    const active = data.people.filter(p => p.status !== 'desligado')
    const thirds = active.filter(p => p.kind === 'terceiro')
    const overdue = reviewOverdue(data.review.dueAt, now)

    return (
        <>
            <StateHeader
                eyebrow={<>{group} · Equipe e permissões <ExampleBadge show={data.dataMode === 'example'} /></>}
                title={teamSentence(active.length, data.leftWithAccess.length)}
                sub={<>Mudanças aqui ainda não alteram PANEL_ADMIN_ROLES. Seu papel: <span className="font-medium text-fg">{me?.roleLabel ?? '…'}</span>.</>}
                pulse={[
                    { label: `terceiros (${thirds.filter(p => p.status === 'expirado').length} expirado)`, value: thirds.length },
                    { label: 'administradores', value: active.filter(p => p.role === 'admin').length },
                    { label: 'áreas', value: data.departments.length },
                ]}
            />
            {intro}

            {data.leftWithAccess.length > 0 && (
                <LeftBanner data={data} onReview={() => { setSection('pessoas'); setView('saiu_rh') }} />
            )}

            <div className="flex flex-wrap gap-4">
                <AdminWindowCard data={data} canManage={canManage} left={adminLeft} />
                <ReviewCard data={data} overdue={overdue} managing={managing} />
            </div>

            <Tabs<Section>
                label="Equipe e permissões"
                value={section}
                onChange={setSection}
                options={[
                    { id: 'pessoas', label: 'Pessoas', count: active.length },
                    { id: 'papeis', label: 'Papéis e permissões' },
                    { id: 'registro', label: 'Registro', count: data.log.length },
                ]}
            />
            {section === 'pessoas' && <PeopleCard data={data} canManage={canManage} managing={managing} now={now} view={view} onView={setView} />}
            {section === 'papeis' && <RoleMatrix />}
            {section === 'registro' && <LogCard data={data} />}

            <HowItWorks>
                <p className="m-0">O papel de administrador é elegível, não fixo: ative só quando precisar, por tempo limitado e com motivo, como no Entra. Sem ele ativo, dá para ver mas não para agir.</p>
                <p className="m-0">Quem sai no RH perde o acesso no mesmo dia. "Desligar acesso" encerra sessões, tira papéis e reatribui pendências, e não tem como desfazer. Terceiros têm empresa e validade; o acesso cai sozinho no dia seguinte.</p>
                <p className="m-0">Papéis e permissões seguem a mesma regra do servidor. Quem pede um estorno nunca aprova o mesmo pedido (Manual Financeiro p. 13); ver como padrinho é só leitura e pede motivo.</p>
                <p className="m-0">O Registro guarda exportações, estornos, descartes, salvaguarda (só o número do caso) e mudanças de acesso.</p>
            </HowItWorks>
        </>
    )
}

function ManageHint() {
    return (
        <p className="m-0 flex items-center gap-1.5 text-[13px] text-muted">
            <ShieldAlert size={15} aria-hidden="true" />Ative o papel de administrador acima para agir.
        </p>
    )
}

function LeftBanner({ data, onReview }: { data: EquipeData; onReview: () => void }) {
    const names = data.people.filter(p => data.leftWithAccess.includes(p.id)).map(p => p.name)
    return (
        <section role="alert" className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[14px] border bg-panel px-4 py-3" style={{ borderColor: 'color-mix(in srgb, var(--red) 45%, transparent)' }}>
            <ShieldAlert size={20} className="flex-none text-red-ink" aria-hidden="true" />
            <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-0.5">
                <span className="text-[15px] font-medium">{data.leftWithAccess.length} {data.leftWithAccess.length === 1 ? 'pessoa saiu' : 'pessoas saíram'} no RH e ainda {data.leftWithAccess.length === 1 ? 'tem' : 'têm'} acesso</span>
                <span className="text-[13px] text-muted">{names.join(', ')}. O acesso cai no mesmo dia.</span>
            </div>
            <button type="button" className={cn(secondaryButton, small)} aria-label="Revisar quem saiu no RH" onClick={onReview}>Revisar</button>
        </section>
    )
}

function AdminWindowCard({ data, canManage, left }: { data: EquipeData; canManage: boolean; left: string | null }) {
    const activate = useAdminAction<ActivateInput>('equipe', 'admin/activate')
    const end = useAdminAction<Record<string, never>>('equipe', 'admin/end')
    const [hours, setHours] = useState<'1' | '4' | '8'>('1')
    const [reason, setReason] = useState('')
    const on = data.adminSession.active && !!left

    return (
        <Card id="eq-admin" title="Papel de administrador" className="flex-[1_1_380px]">
            {!canManage ? (
                <p className="m-0 text-[14px] text-muted">Seu papel só vê as pessoas e o registro.</p>
            ) : on ? (
                <div className="flex flex-wrap items-center gap-3">
                    <Chip tone="warn" dot>ativo · {left}</Chip>
                    <span className="min-w-0 flex-[1_1_200px] text-[13px] text-muted">Motivo: {data.adminSession.reason}</span>
                    <button type="button" className={cn(outlineButton, small)} disabled={end.isPending} onClick={() => end.mutate({})}>Encerrar agora</button>
                </div>
            ) : (
                <form
                    className="flex flex-col gap-2.5"
                    onSubmit={(e: FormEvent) => {
                        e.preventDefault()
                        if (reason.trim().length < 5) return
                        activate.mutate({ horas: Number(hours) as ActivateInput['horas'], motivo: reason.trim() }, { onSuccess: () => setReason('') })
                    }}
                >
                    <Segmented label="Por quanto tempo" value={hours} onChange={setHours} options={ADMIN_HOURS.map(h => ({ id: String(h) as '1' | '4' | '8', label: `${h} h` }))} />
                    <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">
                        Motivo (obrigatório)
                        <input value={reason} onChange={e => setReason(e.target.value)} maxLength={200} placeholder="Ex.: desligar acessos de quem saiu" className="min-h-9 rounded-[10px] border border-line-2 bg-panel px-2.5 text-[14px] font-normal text-fg" />
                    </label>
                    <div>
                        <button type="submit" className={primaryButton} disabled={reason.trim().length < 5 || activate.isPending}>
                            <ShieldCheck aria-hidden="true" />Ativar por {hours} h
                        </button>
                    </div>
                    {activate.isError && <span role="alert" className="text-[13px] text-red-ink">Não deu para ativar. Confira o motivo.</span>}
                </form>
            )}
        </Card>
    )
}

function ReviewCard({ data, overdue, managing }: { data: EquipeData; overdue: boolean; managing: boolean }) {
    const start = useAdminAction<Record<string, never>>('equipe', 'review/start')
    return (
        <Card id="eq-review" title="Revisão de acessos" className="flex-[1_1_320px]">
            <span className="text-[14px]">Última revisão: {shortDate(data.review.lastAt).slice(0, 5)} por {data.review.lastBy}</span>
            <span className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
                Próxima até {shortDate(data.review.dueAt).slice(0, 5)}
                {overdue && <Chip tone="err">atrasada</Chip>}
            </span>
            {data.review.inProgressSince ? (
                <Chip tone="green" dot className="self-start">em andamento desde {shortDate(data.review.inProgressSince)}</Chip>
            ) : (
                <div>
                    <button type="button" className={cn(secondaryButton, small)} disabled={!managing || start.isPending} onClick={() => start.mutate({})}>
                        <ClipboardCheck aria-hidden="true" />Iniciar revisão
                    </button>
                </div>
            )}
        </Card>
    )
}

const VIEWS: { id: PeopleFilter['view']; label: string }[] = [
    { id: 'ativos', label: 'Com acesso' },
    { id: 'terceiros', label: 'Terceiros' },
    { id: 'saiu_rh', label: 'Saíram no RH' },
    { id: 'desligados', label: 'Desligados' },
]

function PeopleCard({ data, canManage, managing, now, view, onView }: {
    data: EquipeData; canManage: boolean; managing: boolean; now: number; view: PeopleFilter['view']; onView: (v: PeopleFilter['view']) => void
}) {
    const [filter, setFilter] = useState<Omit<PeopleFilter, 'view'>>({ q: '', role: 'all', department: 'all' })
    const [openId, setOpenId] = useState<string | null>(null)
    const [inviting, setInviting] = useState(false)
    const closePerson = useCallback(() => setOpenId(null), [])
    const closeInvite = useCallback(() => setInviting(false), [])
    // Here (not in the Drawer) so the toast's "Desfazer" still works after the invite panel closes
    const cancelInvite = useAdminAction<CancelInviteInput>('equipe', 'invite/cancel')
    const full: PeopleFilter = { ...filter, view }
    const list = filterPeople(data.people, full, data.leftWithAccess)
    const person = data.people.find(p => p.id === openId) ?? null
    const page = usePager(list.length)
    const set = (patch: Partial<Omit<PeopleFilter, 'view'>>) => { setFilter(f => ({ ...f, ...patch })); page.reset() }
    const filtersOn = (filter.role !== 'all' ? 1 : 0) + (filter.department !== 'all' ? 1 : 0)

    return (
        <Card
            id="eq-people"
            title="Pessoas"
            aside={canManage && (
                <button type="button" className={ctaButton} disabled={!managing} onClick={() => setInviting(true)} aria-label="Convidar pessoa" title={managing ? undefined : 'Ative o papel de administrador para convidar'}>
                    <UserPlus aria-hidden="true" />Convidar
                </button>
            )}
        >
            {canManage && !managing && <ManageHint />}
            <div className="flex flex-col gap-2.5">
                <div className="flex flex-wrap items-center gap-2">
                    <SearchBox value={filter.q} onChange={q => set({ q })} placeholder="Nome, área, cargo ou empresa" label="Buscar pessoa" />
                    <FiltersPopover active={filtersOn}>
                        <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">
                            Papel
                            <select value={filter.role} onChange={e => set({ role: e.target.value as PeopleFilter['role'] })} className="min-h-9 rounded-[10px] border border-line-2 bg-panel px-2.5 text-[13.5px] font-normal text-fg">
                                <option value="all">Todos os papéis</option>
                                {ADMIN_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 text-[12.5px] font-medium text-muted">
                            Área
                            <select value={filter.department} onChange={e => set({ department: e.target.value })} className="min-h-9 rounded-[10px] border border-line-2 bg-panel px-2.5 text-[13.5px] font-normal text-fg">
                                <option value="all">Todas as áreas</option>
                                {data.departments.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </label>
                    </FiltersPopover>
                </div>
                <SegmentChips<PeopleFilter['view']>
                    label="Quem mostrar"
                    value={view}
                    onChange={v => { onView(v); page.reset() }}
                    options={VIEWS.map(v => ({ value: v.id, label: v.label, count: filterPeople(data.people, { q: '', role: 'all', department: 'all', view: v.id }, data.leftWithAccess).length }))}
                />

                <CompactList label="Pessoas" empty="Ninguém com esses filtros." className="px-1.5">
                    {list.slice(page.start, page.end).map(p => (
                        <PersonRow key={p.id} person={p} now={now} leftInHr={data.leftWithAccess.includes(p.id)} active={p.id === openId} onOpen={() => setOpenId(p.id)} />
                    ))}
                </CompactList>
                <span className="text-[12.5px] text-muted">{list.length} pessoas · e-mails mascarados</span>
                <Pager page={page} />
            </div>

            <Drawer open={!!person} onClose={closePerson} title={person?.name ?? ''} subtitle={person && `${person.department} · ${person.jobTitle}`}>
                {person && <PersonPanel key={person.id} person={person} now={now} leftInHr={data.leftWithAccess.includes(person.id)} canManage={canManage} managing={managing} />}
            </Drawer>

            <Drawer
                open={inviting && managing}
                onClose={closeInvite}
                title="Convidar pessoa"
                subtitle="O convite vai por e-mail; o acesso começa quando a pessoa aceita."
                footer={(
                    <>
                        <button type="button" className={cn(quietButton, small)} onClick={closeInvite}>Cancelar</button>
                        <button type="submit" form="eq-invite" className={cn(primaryButton, small)}>Enviar convite</button>
                    </>
                )}
            >
                {inviting && managing && <InviteForm departments={data.departments} onSent={closeInvite} onUndo={personId => cancelInvite.mutate({ personId })} />}
            </Drawer>
        </Card>
    )
}

function PersonRow({ person: p, now, leftInHr, active, onOpen }: { person: Person; now: number; leftInHr: boolean; active: boolean; onOpen: () => void }) {
    return (
        <li className="border-t border-line first:border-t-0">
            <button
                type="button"
                onClick={onOpen}
                aria-label={`Abrir ${p.name}: acesso, papel e desligar`}
                className={cn('flex min-h-11 w-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-[8px] border-0 bg-transparent px-1.5 py-1.5 text-left font-[inherit] text-fg hover:bg-[var(--hover)]', active && 'bg-[var(--tint)]')}
            >
                <span className="flex min-w-0 flex-[2_1_220px] flex-col leading-[1.3]">
                    <span className="truncate text-[13.5px] font-medium">{p.name}</span>
                    <span className="truncate text-[12.5px] text-muted">{p.department} · {p.jobTitle} · {p.emailMasked}{p.company ? ` · ${p.company}` : ''}</span>
                </span>
                <Chip tone={p.role === 'admin' ? 'warn' : 'green'}>{ROLE_LABELS[p.role as AdminRole]}</Chip>
                <StatusChips person={p} now={now} leftInHr={leftInHr} />
            </button>
        </li>
    )
}

function StatusChips({ person: p, now, leftInHr }: { person: Person; now: number; leftInHr: boolean }) {
    const validity = validityLabel(p, now)
    return (
        <span className="flex flex-wrap gap-1">
            {p.status === 'desligado' && <Chip tone="neutral">Desligado em {p.offboardedAt ? shortDate(p.offboardedAt).slice(0, 5) : '—'}</Chip>}
            {p.status === 'convidado' && <Chip tone="info">convidado</Chip>}
            {p.status === 'ativo' && !validity && <Chip tone="ok" dot>ativo</Chip>}
            {validity && <Chip tone={validity.tone}>{validity.text}</Chip>}
            {leftInHr && <Chip tone="err">saiu no RH</Chip>}
        </span>
    )
}

/** Person detail in the side panel: data, "Cancelar convite" (pending invites only) and "Desligar acesso" with its checklist. */
function PersonPanel({ person: p, now, leftInHr, canManage, managing }: { person: Person; now: number; leftInHr: boolean; canManage: boolean; managing: boolean }) {
    const [confirming, setConfirming] = useState(false)
    const [done, setDone] = useState<OffboardResult | null>(null)
    // The checklist below is the richer result: no toast here
    const offboard = useAdminAction<OffboardInput, OffboardResult>('equipe', 'offboard', { toast: false })
    const cancel = useAdminAction<CancelInviteInput>('equipe', 'invite/cancel')
    const forbidden = (e: unknown) => e instanceof ApiError && e.code === 'forbidden'
    const rows: [string, string][] = [
        ['E-mail', p.emailMasked],
        ['Papel', ROLE_LABELS[p.role as AdminRole]],
        ['Tipo', p.kind === 'terceiro' ? `Terceiro${p.company ? ` · ${p.company}` : ''}` : 'Colaborador'],
        ...(p.validUntil ? [['Válido até', p.validUntil.split('-').reverse().join('/')] as [string, string]] : []),
        ['Último acesso', p.lastSeen ? shortDate(p.lastSeen) : '—'],
    ]

    return (
        <div className="flex flex-col gap-4">
            <StatusChips person={p} now={now} leftInHr={leftInHr} />
            <dl className="m-0 grid gap-x-4 gap-y-2 text-[13.5px]" style={{ gridTemplateColumns: 'max-content 1fr' }}>
                {rows.map(([k, v]) => (
                    <div key={k} className="contents">
                        <dt className="text-muted">{k}</dt>
                        <dd className="m-0 min-w-0 break-words">{v}</dd>
                    </div>
                ))}
            </dl>

            {done && (
                <section aria-label="Resultado do desligamento" role="status" className="flex flex-col gap-1.5 rounded-[12px] bg-[var(--ok-bg)] px-3.5 py-3 text-[13px] text-ok-ink">
                    <span className="font-medium">{done.message}</span>
                    <ul className="m-0 flex list-none flex-col gap-1 p-0">
                        {done.steps.map(step => <li key={step} className="flex items-center gap-1.5"><Check size={14} aria-hidden="true" />{step}</li>)}
                    </ul>
                </section>
            )}

            {canManage && !managing && p.status !== 'desligado' && <ManageHint />}

            {managing && p.status === 'convidado' && !confirming && (
                <div className="flex flex-col gap-1.5">
                    <button type="button" className={cn(outlineButton, small, 'self-start')} disabled={cancel.isPending} onClick={() => cancel.mutate({ personId: p.id })}>
                        <X aria-hidden="true" />Cancelar convite
                    </button>
                    {cancel.isError && <span role="alert" className="text-[13px] text-red-ink">{forbidden(cancel.error) ? 'Ative o papel de administrador.' : 'Não deu para cancelar o convite.'}</span>}
                </div>
            )}

            {canManage && p.status !== 'desligado' && !done && (
                confirming ? (
                    <div className={cn(panelBox, 'flex flex-col gap-2.5')}>
                        <span className="text-[13.5px]">Desligar o acesso de {p.name}? Encerra sessões, tira papéis e reatribui pendências. Não tem como desfazer.</span>
                        <div className="flex flex-wrap justify-end gap-2">
                            <button type="button" className={cn(quietButton, small)} onClick={() => setConfirming(false)}>Cancelar</button>
                            <button
                                type="button"
                                className={cn(primaryButton, small)}
                                style={dangerStyle}
                                disabled={offboard.isPending || !managing}
                                onClick={() => offboard.mutate({ personId: p.id }, { onSuccess: r => { setDone(r); setConfirming(false) } })}
                            >
                                Desligar agora
                            </button>
                        </div>
                        {offboard.isError && <span role="alert" className="text-[13px] text-red-ink">{forbidden(offboard.error) ? 'Ative o papel de administrador para desligar.' : 'Não deu para desligar agora.'}</span>}
                    </div>
                ) : (
                    <button type="button" className={cn(outlineButton, small, 'self-start')} disabled={!managing} onClick={() => setConfirming(true)} title={managing ? undefined : 'Ative o papel de administrador'}>
                        <UserMinus aria-hidden="true" />Desligar acesso
                    </button>
                )
            )}
        </div>
    )
}

/** Fields of "Convidar pessoa"; the submit button lives in the Drawer footer (form="eq-invite"). */
function InviteForm({ departments, onSent, onUndo }: { departments: string[]; onSent: () => void; onUndo: (personId: string) => void }) {
    const invite = useAdminAction<InviteInput, InviteResult>('equipe', 'invite', { undo: (_body, result) => onUndo(result.personId) })
    const [form, setForm] = useState<InviteInput>({ name: '', email: '', department: departments[0] ?? '', role: 'leitura', kind: 'colaborador', company: '', validUntil: '' })
    const [tried, setTried] = useState(false)
    const set = (patch: Partial<InviteInput>) => setForm(f => ({ ...f, ...patch }))
    const third = form.kind === 'terceiro'
    const field = 'flex flex-col gap-1 text-[12.5px] font-medium text-muted'
    const input = 'min-h-9 rounded-[10px] border border-line-2 bg-panel px-2.5 text-[14px] font-normal text-fg'
    const ready = form.name.trim().length >= 3 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) && (!third || ((form.company ?? '').trim().length >= 2 && !!form.validUntil))

    return (
        <form
            id="eq-invite"
            aria-label="Convidar pessoa"
            className="flex flex-col gap-3"
            onSubmit={e => {
                e.preventDefault()
                setTried(true)
                if (!ready || invite.isPending) return
                invite.mutate({ ...form, company: third ? form.company : undefined, validUntil: third ? form.validUntil : undefined }, { onSuccess: onSent })
            }}
        >
            <Segmented label="Tipo" value={form.kind} onChange={kind => set({ kind })} options={[{ id: 'colaborador', label: 'Colaborador' }, { id: 'terceiro', label: 'Terceiro' }]} />
            <label className={field}>Nome<input className={input} value={form.name} onChange={e => set({ name: e.target.value })} maxLength={80} /></label>
            <label className={field}>E-mail<input className={input} type="email" value={form.email} onChange={e => set({ email: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-3">
                <label className={field}>Área
                    <select className={input} value={form.department} onChange={e => set({ department: e.target.value })}>{departments.map(d => <option key={d} value={d}>{d}</option>)}</select>
                </label>
                <label className={field}>Papel
                    <select className={input} value={form.role} onChange={e => set({ role: e.target.value as AdminRole })}>{ADMIN_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select>
                </label>
                {third && <label className={field}>Empresa<input className={input} value={form.company ?? ''} onChange={e => set({ company: e.target.value })} maxLength={80} required /></label>}
                {third && <label className={field}>Válido até<input className={input} type="date" value={form.validUntil ?? ''} onChange={e => set({ validUntil: e.target.value })} required /></label>}
            </div>
            {third && <span className="text-[12.5px] text-muted">Empresa e validade são obrigatórias. O acesso cai sozinho no dia seguinte à validade.</span>}
            {tried && !ready && <span role="alert" className="text-[13px] text-red-ink">Preencha nome, e-mail válido{third ? ', empresa e validade' : ''}.</span>}
            {invite.isError && <span role="alert" className="text-[13px] text-red-ink">Não deu. Confira e-mail, empresa e validade futura.</span>}
        </form>
    )
}

function RoleMatrix() {
    return (
        <Card id="eq-roles" title="Papéis e permissões" aside="mesma regra do servidor">
            <div className="overflow-x-auto rounded-[12px] border border-line">
                <table className="w-full min-w-[980px] border-collapse text-[12.5px]">
                    <caption className="sr-only">Permissões de cada papel do Staff</caption>
                    <thead>
                        <tr className="bg-bg text-left text-muted">
                            <th scope="col" className="px-3 py-2 text-[12px] font-medium">Permissão</th>
                            {ROLE_COLUMNS.map(c => <th key={c.role} scope="col" className="px-2 py-2 text-center text-[12px] font-medium">{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {PERMISSION_GROUPS.map(g => (
                            <GroupRows key={g.label} label={g.label} items={g.items} />
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    )
}

function GroupRows({ label, items }: { label: string; items: (typeof PERMISSION_GROUPS)[number]['items'] }) {
    return (
        <>
            <tr><th scope="rowgroup" colSpan={ROLE_COLUMNS.length + 1} className="border-t border-line bg-raised px-3 py-1.5 text-left text-[12px] font-medium text-muted">{label}</th></tr>
            {items.map(perm => (
                <tr key={perm} className="border-t border-line">
                    <th scope="row" className="px-3 py-1.5 text-left font-medium">{PERMISSION_LABELS[perm]}</th>
                    {ROLE_COLUMNS.map(c => (
                        <td key={c.role} className="px-2 py-1.5 text-center">
                            {roleHas(c.role, perm)
                                ? <Check size={15} className="inline text-green" aria-label="sim" />
                                : <span className="text-dim" aria-label="não">·</span>}
                        </td>
                    ))}
                </tr>
            ))}
        </>
    )
}

function LogCard({ data }: { data: EquipeData }) {
    const [kind, setKind] = useState<LogKind | 'all'>('all')
    const list = filterLog(data.log, kind)
    const page = usePager(list.length)
    const options = [{ value: 'all' as const, label: 'Todos', count: data.log.length }, ...LOG_KINDS.map(k => ({ value: k, label: LOG_LABELS[k], count: filterLog(data.log, k).length }))]
    const tone = (k: LogKind): Tone => (k === 'salvaguarda' ? 'err' : k === 'admin' || k === 'estorno' ? 'warn' : k === 'retencao' ? 'ok' : k === 'export' || k === 'vercomo' ? 'info' : 'neutral')

    return (
        <Card id="eq-log" title="Registro">
            <SegmentChips<LogKind | 'all'> label="Tipo" value={kind} onChange={k => { setKind(k); page.reset() }} options={options} />
            <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
                {list.slice(page.start, page.end).map((e, i) => (
                    <li key={`${e.at}-${i}`} className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1 border-b border-line py-1.5 text-[13px] last:border-b-0">
                        <span className="tabular w-[88px] flex-none text-muted">{shortDate(e.at)}</span>
                        <Chip tone={tone(e.kind)}>{LOG_LABELS[e.kind]}</Chip>
                        <span className="min-w-0 flex-[1_1_300px]"><span className="font-medium">{e.who}</span> · {e.text}</span>
                    </li>
                ))}
                {list.length === 0 && <li className="py-6 text-center text-[14px] text-muted">Nada desse tipo.</li>}
            </ul>
            <Pager page={page} />
        </Card>
    )
}
